import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { adminProductsAPI, categoryAPI } from '../../services/api.ts';
import { Product, Category } from '../../types/index.ts';
import LoadingSpinner from '../../components/LoadingSpinner.tsx';
import H2GOLogo from '../../components/H2GOLogo.tsx';
import ImageUpload from '../../components/ImageUpload.tsx';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import ProductPermanentDeleteModal from '../../components/ProductPermanentDeleteModal.tsx';

interface ProductFormData {
  name: string;
  description: string;
  type: 'gallon' | 'bottle';
  size: string;
  capacity: number;
  price: number;
  deposit: number;
  categoryId: number | undefined;
  brand: string;
  stock: number;
  isActive: boolean;
  image?: string;
}

const AdminProducts: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [showActiveOnly, setShowActiveOnly] = useState<boolean | null>(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    type: 'bottle',
    size: '',
    capacity: 0,
    price: 0,
    deposit: 0,
    categoryId: undefined,
    brand: 'H2GO',
    stock: 0,
    isActive: true,
    image: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  
  // Modal states
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryAPI.getActiveCategories();
      setCategories(response.data.data.categories);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Helper function to get category name by ID
  const getCategoryName = (categoryId: number | undefined) => {
    if (!categoryId) return 'No Category';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  // Helper function to get category image by ID
  const getCategoryImage = (categoryId: number | undefined) => {
    if (!categoryId) return null;
    const category = categories.find(cat => cat.id === categoryId);
    return category?.category_image || null;
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: currentPage,
        limit: 10,
      };
      
      if (searchTerm) params.search = searchTerm;
      if (selectedType) params.type = selectedType;
      if (showActiveOnly !== null) params.isActive = showActiveOnly;

      const response = await adminProductsAPI.getAllProducts(params);
      setProducts(response.data.data.products);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalItems(response.data.data.pagination.totalItems);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedType, showActiveOnly]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchProducts();
    fetchCategories();
  }, [isAuthenticated, user, navigate, fetchProducts, fetchCategories]);

  // Populate form data when editing a product
  useEffect(() => {
    if (editingProduct) {
      setFormData({
        name: editingProduct.name || '',
        description: editingProduct.description || '',
        type: editingProduct.type || 'bottle',
        size: editingProduct.size || '',
        capacity: editingProduct.capacity || 0,
        price: editingProduct.price || 0,
        deposit: editingProduct.deposit || 0,
        categoryId: editingProduct.categoryId || undefined,
        brand: editingProduct.brand || 'H2GO',
        stock: editingProduct.stock || 0,
        isActive: editingProduct.isActive !== undefined ? editingProduct.isActive : true,
        image: editingProduct.image || ''
      });
    } else {
      // Reset form data when not editing
      setFormData({
        name: '',
        description: '',
        type: 'bottle',
        size: '',
        capacity: 0,
        price: 0,
        deposit: 0,
        categoryId: undefined,
        brand: 'H2GO',
        stock: 0,
        isActive: true,
        image: ''
      });
    }
  }, [editingProduct]);

  // Handle toggle status with modal confirmation
  const handleToggleStatus = (product: Product) => {
    setProductToDelete(product);
    setShowConfirmationModal(true);
  };

  // Handle permanent delete with modal confirmation
  const handlePermanentDelete = (product: Product) => {
    setProductToDelete(product);
    setShowPermanentDeleteModal(true);
  };

  // Confirm toggle status
  const confirmToggleStatus = async () => {
    if (!productToDelete) return;
    
    setDeleteLoading(true);
    try {
      await adminProductsAPI.toggleProductStatus(productToDelete.id);
      fetchProducts();
    } catch (error: any) {
      console.error('Error updating product status:', error);
    } finally {
      setDeleteLoading(false);
      setShowConfirmationModal(false);
      setProductToDelete(null);
    }
  };

  // Confirm permanent delete
  const confirmPermanentDelete = async () => {
    if (!productToDelete) return;
    
    setDeleteLoading(true);
    try {
      await adminProductsAPI.permanentDeleteProduct(productToDelete.id);
      fetchProducts();
    } catch (error: any) {
      console.error('Error permanently deleting product:', error);
    } finally {
      setDeleteLoading(false);
      setShowPermanentDeleteModal(false);
      setProductToDelete(null);
    }
  };

  // Close confirmation modals
  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setProductToDelete(null);
  };

  const closePermanentDeleteModal = () => {
    setShowPermanentDeleteModal(false);
    setProductToDelete(null);
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <H2GOLogo className="h-12 w-auto text-blue-600" />
            <h1 className="text-4xl font-extrabold text-gray-900">Product Management</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Product
            </button>
            <div className="text-right">
              <p className="text-lg font-medium text-gray-700">Total Products: {totalItems}</p>
              <p className="text-sm text-gray-500">Manage product catalog</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="bottle">Bottle</option>
                <option value="gallon">Gallon</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={showActiveOnly === null ? '' : showActiveOnly.toString()}
                onChange={(e) => setShowActiveOnly(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            
            <div className="flex items-end space-x-2">
              <button
                onClick={fetchProducts}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Search
              </button>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedType('');
                  setShowActiveOnly(null);
                  setCurrentPage(1);
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Products</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchProducts}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-xl mb-4">📦</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
              <p className="text-gray-600 mb-4">No products match your current filters.</p>
              <button
                onClick={() => setShowModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add First Product
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-lg flex items-center justify-center mr-4 overflow-hidden">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full bg-blue-100 flex items-center justify-center">
                                <H2GOLogo className="h-8 w-8 text-blue-600" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-500">{product.size}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          product.type === 'gallon' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {product.type}
                        </span>
                      </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getCategoryImage(product.categoryId) ? (
                        <img 
                          src={getCategoryImage(product.categoryId)!} 
                          alt={getCategoryName(product.categoryId)}
                          className="h-6 w-6 rounded mr-2 object-cover"
                        />
                      ) : (
                        <div className="h-6 w-6 rounded mr-2 bg-gray-200 flex items-center justify-center">
                          <span className="text-xs text-gray-500">?</span>
                        </div>
                      )}
                      <span className="text-sm text-gray-900">{getCategoryName(product.categoryId)}</span>
                    </div>
                  </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">₹{product.price}</div>
                        {product.deposit > 0 && (
                          <div className="text-xs text-gray-500">+ ₹{product.deposit} deposit</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${
                          product.stock > 10 ? 'text-green-600' : 
                          product.stock > 0 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {product.stock}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          product.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingProduct(product);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Edit product"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleToggleStatus(product)}
                            className={`p-1 rounded hover:bg-gray-50 ${
                              product.isActive
                                ? 'text-red-600 hover:text-red-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={product.isActive ? 'Deactivate product' : 'Activate product'}
                          >
                            {product.isActive ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(product)}
                            className="text-red-800 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete product"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span> ({totalItems} total)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProduct(null);
                  setFormData({
                    name: '',
                    description: '',
                    type: 'bottle',
                    size: '',
                    capacity: 0,
                    price: 0,
                    deposit: 0,
                    categoryId: undefined,
                    brand: 'H2GO',
                    stock: 0,
                    isActive: true,
                    image: ''
                  });
                  setFormErrors({});
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSubmitting(true);
              setFormErrors({});
              
              // Client-side validation
              const newErrors: Record<string, string> = {};
              
              if (!formData.name.trim()) {
                newErrors.name = 'Product name is required';
              } else if (formData.name.trim().length < 2) {
                newErrors.name = 'Product name must be at least 2 characters long';
              }
              
              if (!formData.size.trim()) {
                newErrors.size = 'Size is required';
              }
              
              if (formData.capacity <= 0) {
                newErrors.capacity = 'Capacity must be greater than 0';
              }
              
              if (formData.price <= 0) {
                newErrors.price = 'Price must be greater than 0';
              }
              
              if (formData.stock < 0) {
                newErrors.stock = 'Stock cannot be negative';
              }
              
              if (Object.keys(newErrors).length > 0) {
                setFormErrors(newErrors);
                setSubmitting(false);
                return;
              }
              
              try {
                if (editingProduct) {
                  await adminProductsAPI.updateProduct(editingProduct.id, formData);
                  alert('Product updated successfully!');
                } else {
                  await adminProductsAPI.createProduct(formData);
                  alert('Product created successfully!');
                }
                
                setShowModal(false);
                setEditingProduct(null);
                setFormData({
                  name: '',
                  description: '',
                  type: 'bottle',
                  size: '',
                  capacity: 0,
                  price: 0,
                  deposit: 0,
                  categoryId: undefined,
                  brand: 'H2GO',
                  stock: 0,
                  isActive: true,
                  image: ''
                });
                fetchProducts();
              } catch (error: any) {
                console.error('Error saving product:', error);
                
                // Handle different types of errors
                if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                  // Handle validation errors from server
                  const validationErrors: Record<string, string> = {};
                  error.response.data.errors.forEach((err: any) => {
                    const fieldMap: Record<string, string> = {
                      'name': 'name',
                      'description': 'description',
                      'type': 'type',
                      'size': 'size',
                      'capacity': 'capacity',
                      'price': 'price',
                      'deposit': 'deposit',
                      'stock': 'stock',
                      'categoryId': 'categoryId'
                    };
                    
                    const fieldName = fieldMap[err.path] || err.path;
                    validationErrors[fieldName] = err.msg;
                  });
                  setFormErrors(validationErrors);
                } else {
                  const errorMessage = error.response?.data?.message || 'Failed to save product. Please try again.';
                  setFormErrors({ submit: errorMessage });
                }
              } finally {
                setSubmitting(false);
              }
            }} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Product Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.name;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter product name"
                    required
                  />
                  {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter product description"
                  />
                </div>

                {/* Product Image */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                  <ImageUpload
                    currentImageUrl={formData.image}
                    onImageUpload={(url) => setFormData({ ...formData, image: url })}
                    onImageRemove={() => setFormData({ ...formData, image: '' })}
                    disabled={submitting}
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'gallon' | 'bottle' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="bottle">Bottle</option>
                    <option value="gallon">Gallon</option>
                  </select>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Size *</label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => {
                      setFormData({ ...formData, size: e.target.value });
                      if (formErrors.size) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.size;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.size ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 1L, 20L"
                    required
                  />
                  {formErrors.size && <p className="mt-1 text-sm text-red-600">{formErrors.size}</p>}
                </div>

                {/* Capacity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capacity (L) *</label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => {
                      setFormData({ ...formData, capacity: parseFloat(e.target.value) || 0 });
                      if (formErrors.capacity) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.capacity;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.capacity ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                    min="0"
                    step="0.1"
                    required
                  />
                  {formErrors.capacity && <p className="mt-1 text-sm text-red-600">{formErrors.capacity}</p>}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.categoryId || ''}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: parseFloat(e.target.value) || 0 });
                      if (formErrors.price) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.price;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.price ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                  />
                  {formErrors.price && <p className="mt-1 text-sm text-red-600">{formErrors.price}</p>}
                </div>

                {/* Deposit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deposit (₹)</label>
                  <input
                    type="number"
                    value={formData.deposit}
                    onChange={(e) => setFormData({ ...formData, deposit: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Stock */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => {
                      setFormData({ ...formData, stock: parseInt(e.target.value) || 0 });
                      if (formErrors.stock) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.stock;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.stock ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                    min="0"
                    required
                  />
                  {formErrors.stock && <p className="mt-1 text-sm text-red-600">{formErrors.stock}</p>}
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="H2GO"
                  />
                </div>

                {/* Active Status */}
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Active Product</span>
                  </label>
                </div>
              </div>

              {(formErrors.submit || Object.keys(formErrors).some(key => key !== 'submit')) && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  {formErrors.submit && (
                    <p className="text-red-600 text-sm mb-2">{formErrors.submit}</p>
                  )}
                  {Object.keys(formErrors).some(key => key !== 'submit') && (
                    <div className="text-red-600 text-sm">
                      <p className="font-medium mb-1">Please fix the following issues:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {Object.entries(formErrors)
                          .filter(([key]) => key !== 'submit')
                          .map(([key, message]) => (
                            <li key={key}>{message}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    setFormData({
                      name: '',
                      description: '',
                      type: 'bottle',
                      size: '',
                      capacity: 0,
                      price: 0,
                      deposit: 0,
                      categoryId: undefined,
                      brand: 'H2GO',
                      stock: 0,
                      isActive: true,
                      image: ''
                    });
                    setFormErrors({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Status Toggle */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={closeConfirmationModal}
        onConfirm={confirmToggleStatus}
        title={productToDelete ? (productToDelete.isActive ? 'Deactivate Product' : 'Activate Product') : ''}
        message={productToDelete ? `Are you sure you want to ${productToDelete.isActive ? 'deactivate' : 'activate'} the product "${productToDelete.name}"?` : ''}
        confirmText={productToDelete ? (productToDelete.isActive ? 'Deactivate' : 'Activate') : ''}
        type={productToDelete?.isActive ? 'warning' : 'info'}
        isLoading={deleteLoading}
      />

      {/* Permanent Delete Modal */}
      <ProductPermanentDeleteModal
        isOpen={showPermanentDeleteModal}
        onClose={closePermanentDeleteModal}
        onConfirm={confirmPermanentDelete}
        productName={productToDelete?.name || ''}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default AdminProducts;
